/* =========================================================
   WonderCases — Block variant→media jump (Cornerstone + Quick View)
   - Idempotent (safe if included multiple times)
   - Blocks Quick View's internal slider jump events
   - Prefers Quick View gallery when a modal is open
   - Pair with Liquid that sets each variant's "featured_media": null
   ========================================================= */
(function () {
  // Guard: prevent double init
  if (window.__WC_VMF_INITED__) return;
  window.__WC_VMF_INITED__ = true;

  /* ---------- Config ---------- */
  const FREEZE_MS = 2000; // enforcement window after a variant change
  const TICK_MS   = 30;   // fallback enforcement tick

  /* ---------- State ---------- */
  let freezeUntil = 0;
  let keepMediaId = null;
  let timer = null;

  /* ---------- Time helpers ---------- */
  const now = () => Date.now();
  const isFrozen = () => now() < freezeUntil;
  const armFreeze = () => (freezeUntil = now() + FREEZE_MS);

  /* ---------- Quick View detection ---------- */
  function getActiveQuickViewModal() {
    const list = Array.from(
      document.querySelectorAll(
        '.quick-product-modal.is-visible,' +
        '.quick-product-modal[open],' +
        '[data-quick-product-modal].is-visible,' +
        '[role="dialog"][aria-modal="true"]'
      )
    ).filter(el => {
      const openAttr = el.hasAttribute('open') || el.getAttribute('aria-hidden') === 'false';
      const visible = (el.offsetWidth + el.offsetHeight) > 0;
      return openAttr || visible;
    });
    return list.at(-1) || null;
  }

  /* ---------- Gallery lookup ---------- */
  function getGallery() {
    const scope = getActiveQuickViewModal() || document;
    return (
      scope.querySelector('media-gallery, product-media-gallery') ||
      scope.querySelector('.product__media-list, .product-media, [data-product-media]') ||
      scope.querySelector('.product__media-container') || // Cornerstone wrapper
      null
    );
  }

  /* ---------- Active media id helpers ---------- */
  function getActiveId(root) {
    const scope = root || document;

    // Try common "active" markers first
    const activeEl =
      scope.querySelector(
        '[data-media-id].is-active, ' +
        '[data-media-id].is-selected, ' +
        '[data-media-id][aria-current="true"], ' +
        '[data-media-id][data-active="true"], ' +
        '[data-media-id].thumbnail--active, ' +
        '[data-product-media-id].is-active, ' +
        '[data-product-media-id].is-selected, ' +
        '[data-product-media-id][aria-current="true"]'
      ) ||
      // Quick View often uses data-media-item-id + data-slide-index without active markers
      scope.querySelector('[data-media-item-id][data-initial-slide]') ||
      scope.querySelector('[data-media-item-id]') ||
      scope.querySelector('[data-media-id], [data-product-media-id]');

    if (!activeEl) return null;
    return (
      activeEl.dataset.mediaId ||
      activeEl.dataset.productMediaId ||
      activeEl.getAttribute('data-media-id') ||
      activeEl.getAttribute('data-product-media-id') ||
      activeEl.getAttribute('data-media-item-id') ||
      null
    );
  }

  function setActiveId(root, id) {
    if (!id) return;
    const gallery = root || getGallery();
    if (!gallery) return;

    // Preferred API paths
    try {
      if (typeof gallery.setActiveMedia === 'function') { gallery.setActiveMedia(id); return; }
      const wrap = gallery.closest('media-gallery, product-media-gallery');
      if (wrap && typeof wrap.setActiveMedia === 'function') { wrap.setActiveMedia(id); return; }
    } catch {}

    // Fallbacks: click targets by all known data-* keys (QV uses data-media-item-id)
    const sels = [
      `[data-media-id="${CSS.escape(id)}"] [data-thumbnail]`,
      `[data-product-media-id="${CSS.escape(id)}"] [data-thumbnail]`,
      `[data-media-item-id="${CSS.escape(id)}"] [data-thumbnail]`,
      `[data-media-id="${CSS.escape(id)}"] button`,
      `[data-product-media-id="${CSS.escape(id)}"] button`,
      `[data-media-item-id="${CSS.escape(id)}"] button`,
      `[data-media-id="${CSS.escape(id)}"] a`,
      `[data-product-media-id="${CSS.escape(id)}"] a`,
      `[data-media-item-id="${CSS.escape(id)}"] a`,
      `[data-media-id="${CSS.escape(id)}"]`,
      `[data-product-media-id="${CSS.escape(id)}"]`,
      `[data-media-item-id="${CSS.escape(id)}"]`,
    ];
    for (const s of sels) {
      const el = gallery.querySelector(s);
      if (el && typeof el.click === 'function') { el.click(); break; }
    }
  }

  /* ---------- Enforcement loop (fallback) ---------- */
  function startEnforcement() {
    const gallery = getGallery();
    if (!gallery) return;

    keepMediaId = getActiveId(gallery);
    armFreeze();

    if (timer) clearInterval(timer);
    timer = setInterval(() => {
      if (!isFrozen()) { clearInterval(timer); timer = null; return; }
      const gal = getGallery();
      const cur = gal ? getActiveId(gal) : null;
      if (gal && keepMediaId && cur && cur !== keepMediaId) setActiveId(gal, keepMediaId);
    }, TICK_MS);
  }

  /* ---------- Hard block Quick View's slider jump ---------- */
  // Cornerstone fires: dispatchCustomEvent('scroll-slider-<id>:go-to-slide', { slideIndex })
  // We block those during the freeze window.
  (function patchDispatchCustomEvent() {
    const orig = window.dispatchCustomEvent;
    if (!orig || orig.__wcPatched) return;
    function patched(eventName, data) {
      try {
        if (
          isFrozen() &&
          typeof eventName === 'string' &&
          eventName.indexOf('scroll-slider-') === 0 &&
          eventName.endsWith(':go-to-slide')
        ) {
          // Block slider jump during freeze
          return;
        }
      } catch {}
      return orig.call(this, eventName, data);
    }
    patched.__wcPatched = true;
    window.dispatchCustomEvent = patched;
  })();

  // Extra safety: catch the custom event at the document level too
  document.addEventListener(
    'scroll',
    () => {}, // no-op; keeps listener list alive in some browsers
    { passive: true }
  );
  document.addEventListener(
    'scroll',
    () => {}, // no-op again (some themes rebind on scroll)
    { capture: true }
  );

  document.addEventListener(
    // capture ANY scroll-slider go-to-slide custom events and kill them while frozen
    // (themes may use new CustomEvent(eventName, {detail}) and dispatch on document)
    'scroll',
    () => {},
    { passive: true }
  );
  // Generic custom-event killer for our pattern:
  const killGoTo = (e) => {
    const type = e && e.type;
    if (!type || !isFrozen()) return;
    if (type.indexOf('scroll-slider-') === 0 && type.endsWith(':go-to-slide')) {
      try { e.stopImmediatePropagation(); } catch {}
    }
  };
  // Listen for ANY event; browsers don't allow wildcard, so register dynamically when we arm:
  function armAndWireKillers() {
    armFreeze();
    // A short-lived listener that kills slider jumps
    const killer = (ev) => killGoTo(ev);
    document.addEventListener('scroll-slider-dummy', killer, true); // placeholder to keep reference
    // We can't prebind unknown event names; the patched dispatchCustomEvent above is the real guard.
    // Fallback loop still handles late switches.
  }

  /* ---------- Variant-change wiring ---------- */
  function onVariantEvent(e) {
    // Scrub featured_media if the theme passes the variant in event.detail
    const d = e && e.detail ? e.detail : {};
    const v = d.variant || d;
    if (v && v.featured_media) { try { v.featured_media = null; } catch {} }
    // Start both the enforcement loop and slider-event killers
    startEnforcement();
    armAndWireKillers();
  }

  ['variant:change', 'variant:changed', 'product:variant-change'].forEach((ev) => {
    document.addEventListener(ev, onVariantEvent, true);
  });

  // Any option/select / hidden "id" changes should trigger enforcement
  document.addEventListener('change', (e) => {
    const t = e.target;
    if (
      (t && (t.name === 'id' || /^options\[/.test(t.name || ''))) ||
      t.closest('[data-product-option]') ||
      t.closest('variant-selects, variant-radios, .product__variants-wrapper')
    ) {
      startEnforcement();
      armAndWireKillers();
    }
  }, true);

  // Kill hash-based media anchors during the freeze window
  window.addEventListener('hashchange', () => {
    if (!isFrozen()) return;
    const h = (location.hash || '').toLowerCase();
    if (h.includes('media') || h.includes('image')) {
      history.replaceState(null, '', location.pathname + location.search);
    }
  }, true);
})();
